import React, { useState, useEffect } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CustomIcon from "../../../Components/CustomIcon";
import { getDataImportTypes } from "../../../Api/ResourceOnboardingApi";
import { usePermissions } from "../../../Context/PermissionContext";
import { getAccessibleImportTypes } from "../importTypeAccess";

const Step1SelectResourceType = ({ onSelect }) => {
    const [importTypes, setImportTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { hasPermission } = usePermissions();

    useEffect(() => {
        const fetchImportTypes = async () => {
            try {
                setLoading(true);
                const importTypesResponse = await getDataImportTypes();

                const rawTypes = importTypesResponse?.response?.data || [];

                // Defense in depth: the backend already returns only accessible
                // types, but re-filter by the per-type feature here in case of a
                // stale/shared response. Ungated types always pass.
                setImportTypes(getAccessibleImportTypes(rawTypes, hasPermission));
            } catch (error) {
                console.error('Error fetching import types:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchImportTypes();
        // Fetch once on mount. hasPermission reads live permission state at call
        // time, and it is an unstable reference from PermissionProvider (re-created
        // on every Redux-driven render), so depending on it would refetch on
        // unrelated state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResourceOnboarding = (importTypeData) => {
        onSelect(importTypeData);
    };

    return (
        <Grid2 container flexDirection='column' gap={2}>
            {/* Instructions */}
            <Typography
                variant="fw400s16c000000"
                sx={{
                    color: '#686868',
                    lineHeight: '20px',
                }}
            >
                Select what data you want to upload from options below
            </Typography>

            {/* Onboarding Options */}
            <Grid2 container spacing={2}>
                {loading ? (
                    <Grid2 size={12}>
                        <Typography variant="fw400s14c000000" sx={{ color: '#A2A2A2', textAlign: 'center', py: 4 }}>
                            Loading import types...
                        </Typography>
                    </Grid2>
                ) : importTypes.length === 0 ? (
                    <Grid2 size={12}>
                        <Typography variant="fw400s14c000000" sx={{ color: '#A2A2A2', textAlign: 'center', py: 4 }}>
                            No import types available
                        </Typography>
                    </Grid2>
                ) : (
                    importTypes.map((importType) => (
                        <Grid2 key={importType.id} size={{ xs: 12, md: 6 }}>
                            <Box
                                onClick={() => handleResourceOnboarding(importType)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '24px',
                                    backgroundColor: '#F9F9F9',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: '#EFF0FF',
                                        '& .add-icon': {
                                            color: '#4D44D8',
                                        },
                                        '& .option-text': {
                                            color: '#4D44D8',
                                        }
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CustomIcon name={importType.icon} width={23} height={23} />
                                    <Typography className="option-text" variant="fw600s16c000000" sx={{ color: '#494949', transition: 'color 0.2s' }}>
                                        {importType.label}
                                    </Typography>
                                </Box>
                                <AddIcon className="add-icon" sx={{ fontSize: 24, color: '#A2A2A2', transition: 'color 0.2s' }} />
                            </Box>
                        </Grid2>
                    ))
                )}
            </Grid2>
        </Grid2>
    );
};

export default Step1SelectResourceType;
